<template>
    <header-index />

    <!-- Hero 大图区域 - 写手/创作意境 -->
    <section class="relative w-full bg-cover bg-center" style="height:500px;"
      :style="`background-image: url(${cmsConfig.websiteName}/images/services/banner.jpg)`">
      <div class="absolute inset-0 bg-gradient-to-r from-gray-900/85 via-gray-800/70 to-gray-700/50"></div>
      <div class="container mx-auto px-4 h-full flex items-center justify-center relative z-10">
        <div class="max-w-4xl text-center" data-aos="fade-up">
          <h1 class="text-5xl md:text-6xl font-bold text-white mb-6">Our Authors</h1>
          <p class="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            Industry experts who share knowledge through professional insights and practical experience from leafly
          </p>
        </div>
      </div>
    </section>

    <!-- 团队整体介绍（包装专业 + 写作能力） -->
    <section class="py-20 bg-white">
      <div class="container mx-auto px-4">
        <div class="max-w-5xl mx-auto text-center" data-aos="fade-up">
          <h2 class="text-4xl font-bold text-gray-800 mb-6">The Minds Behind Our Content</h2>
          <p class="text-lg text-gray-600 leading-relaxed mb-6 max-w-4xl mx-auto">
            Our authors are more than packaging professionals — they are industry practitioners who translate hands-on expertise into actionable insights. From packaging processes and international trade to CBD compliance and digital strategy, each writer brings a unique specialty to the table. They don't just know the industry; they write about it with clarity, depth, and real-world perspective, helping you stay informed and make better decisions.
          </p>
          <!-- 团队数据亮点 -->
          <div class="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl mx-auto mt-10">
            <div v-for="stat in teamStats" :key="stat.label" class="text-center">
              <div class="text-3xl font-bold text-green-600 mb-1">{{ stat.value }}</div>
              <div class="text-sm text-gray-500">{{ stat.label }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 作者左右布局 -->
    <section class="py-20 bg-gray-50">
      <div class="container mx-auto px-4">
        <div class="max-w-6xl mx-auto space-y-20">
          <!-- Joanna -->
          <div class="flex flex-col lg:flex-row gap-8 lg:gap-12 items-center bg-white rounded-2xl shadow-lg overflow-hidden" data-aos="fade-up">
            <div class="w-full lg:w-5/12">
              <div class="aspect-[4/5] overflow-hidden">
                <img :src="joanna.imageUrl"
                  :alt="joanna.name"
                  class="w-full h-full object-cover hover:scale-105 transition-transform duration-700">
              </div>
            </div>
            <div class="w-full lg:w-7/12 p-8 lg:p-10 lg:pr-12">
              <div class="flex items-center justify-between mb-4">
                <div>
                  <h3 class="text-3xl font-bold text-gray-800">{{ joanna.name }}</h3>
                  <p class="text-green-600 font-medium text-lg">{{ joanna.position }}</p>
                </div>
                <a v-if="joanna.linkedin"
                  :href="joanna.linkedin"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-blue-600 hover:text-blue-800 transition-colors flex-shrink-0"
                  :title="`${joanna.name}'s LinkedIn`">
                  <svg class="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
              <div class="space-y-4">
                <p class="text-gray-700 leading-relaxed text-lg">As a packaging industry expert with over a decade of experience, Joanna brings deep knowledge of packaging processes, materials, and design to every article she writes. Her writing bridges the gap between technical complexity and practical understanding, offering readers clear guidance from concept to production.</p>
                <p class="text-gray-700 leading-relaxed text-lg">She is the go-to author for in-depth packaging insights — covering everything from material selection to market trends — drawing on years of hands-on industry experience to deliver content that truly informs.</p>
              </div>
              <div class="flex flex-wrap gap-2 mt-5">
                <span v-for="tag in joanna.expertise" :key="tag"
                  class="px-3 py-1.5 bg-green-50 text-green-700 text-sm rounded-full font-medium">
                  {{ tag }}
                </span>
              </div>
            </div>
          </div>

          <!-- Joyce -->
          <div class="flex flex-col lg:flex-row-reverse gap-8 lg:gap-12 items-center bg-white rounded-2xl shadow-lg overflow-hidden" data-aos="fade-up">
            <div class="w-full lg:w-5/12">
              <div class="aspect-[4/5] overflow-hidden">
                <img :src="joyce.imageUrl"
                  :alt="joyce.name"
                  class="w-full h-full object-cover hover:scale-105 transition-transform duration-700">
              </div>
            </div>
            <div class="w-full lg:w-7/12 p-8 lg:p-10 lg:pl-12">
              <div class="flex items-center justify-between mb-4">
                <div>
                  <h3 class="text-3xl font-bold text-gray-800">{{ joyce.name }}</h3>
                  <p class="text-green-600 font-medium text-lg">{{ joyce.position }}</p>
                </div>
                <a v-if="joyce.linkedin"
                  :href="joyce.linkedin"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-blue-600 hover:text-blue-800 transition-colors flex-shrink-0"
                  :title="`${joyce.name}'s LinkedIn`">
                  <svg class="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
              <div class="space-y-4">
                <p class="text-gray-700 leading-relaxed text-lg">Joyce combines her extensive knowledge of US packaging regulations and international trade with a talent for clear, practical writing. Having served leading American companies like 360Pack, AE Global, ASTM Cannabis, and BASCO, Inc., she brings real-world trade experience to every piece she writes.</p>
                <p class="text-gray-700 leading-relaxed text-lg">Her articles focus on helping businesses navigate the complexities of the US market, making regulatory compliance accessible and actionable for readers around the world.</p>
              </div>
              <div class="flex flex-wrap gap-2 mt-5">
                <span v-for="tag in joyce.expertise" :key="tag"
                  class="px-3 py-1.5 bg-green-50 text-green-700 text-sm rounded-full font-medium">
                  {{ tag }}
                </span>
              </div>
            </div>
          </div>

          <!-- Jane -->
          <div class="flex flex-col lg:flex-row gap-8 lg:gap-12 items-center bg-white rounded-2xl shadow-lg overflow-hidden" data-aos="fade-up">
            <div class="w-full lg:w-5/12">
              <div class="aspect-[4/5] overflow-hidden">
                <img :src="jane.imageUrl"
                  :alt="jane.name"
                  class="w-full h-full object-cover hover:scale-105 transition-transform duration-700">
              </div>
            </div>
            <div class="w-full lg:w-7/12 p-8 lg:p-10 lg:pr-12">
              <div class="flex items-center justify-between mb-4">
                <div>
                  <h3 class="text-3xl font-bold text-gray-800">{{ jane.name }}</h3>
                  <p class="text-green-600 font-medium text-lg">{{ jane.position }}</p>
                </div>
                <a v-if="jane.linkedin"
                  :href="jane.linkedin"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-blue-600 hover:text-blue-800 transition-colors flex-shrink-0"
                  :title="`${jane.name}'s LinkedIn`">
                  <svg class="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
              <div class="space-y-4">
                <p class="text-gray-700 leading-relaxed text-lg">Jane is a CBD packaging specialist with deep expertise in Mylar bags, Rigid boxes, and Child-Resistant (CR) packaging design. Her writing reflects years of hands-on experience in production, structural design, and printing processes — always with an eye on cost-effective solutions that don't compromise quality.</p>
                <p class="text-gray-700 leading-relaxed text-lg">When she writes about CBD and vape packaging, readers get trustworthy, regulation-aware guidance from someone who understands both the technical and compliance sides of the industry.</p>
              </div>
              <div class="flex flex-wrap gap-2 mt-5">
                <span v-for="tag in jane.expertise" :key="tag"
                  class="px-3 py-1.5 bg-green-50 text-green-700 text-sm rounded-full font-medium">
                  {{ tag }}
                </span>
              </div>
            </div>
          </div>

          <!-- Jsper Fang -->
          <div class="flex flex-col lg:flex-row-reverse gap-8 lg:gap-12 items-center bg-white rounded-2xl shadow-lg overflow-hidden" data-aos="fade-up">
            <div class="w-full lg:w-5/12">
              <div class="aspect-[4/5] overflow-hidden">
                <img :src="jsper.imageUrl"
                  :alt="jsper.name"
                  class="w-full h-full object-cover hover:scale-105 transition-transform duration-700">
              </div>
            </div>
            <div class="w-full lg:w-7/12 p-8 lg:p-10 lg:pl-12">
              <div class="flex items-center justify-between mb-4">
                <div>
                  <h3 class="text-3xl font-bold text-gray-800">{{ jsper.name }}</h3>
                  <p class="text-green-600 font-medium text-lg">{{ jsper.position }}</p>
                </div>
                <a v-if="jsper.linkedin"
                  :href="jsper.linkedin"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-blue-600 hover:text-blue-800 transition-colors flex-shrink-0"
                  :title="`${jsper.name}'s LinkedIn`">
                  <svg class="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
              <div class="space-y-4">
                <p class="text-gray-700 leading-relaxed text-lg">Jsper is a CRM and digital marketing specialist who brings a modern, strategic lens to packaging industry content. With expertise spanning packaging processes, SEO, and digital promotion, his writing helps readers discover the right solutions and navigate the packaging landscape with confidence.</p>
                <p class="text-gray-700 leading-relaxed text-lg">He combines deep industry knowledge with effective content strategies, making complex topics accessible and ensuring that every article reaches and resonates with the right audience.</p>
              </div>
              <div class="flex flex-wrap gap-2 mt-5">
                <span v-for="tag in jsper.expertise" :key="tag"
                  class="px-3 py-1.5 bg-green-50 text-green-700 text-sm rounded-full font-medium">
                  {{ tag }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <footer-index />
</template>

<script setup lang="ts" name="SinglePageAuthors">
    import type { SinglePage } from '~/types';
    import cmsConfig from '~/cmsConfig'
    import cmsUtils from '~/utils/cmsUtils';
    import headerIndex from '../../components/header/header-index.vue'
    import footerIndex from '../../components/footer/footer-index.vue'
    import sectionCTA from '../../components/common/sectionCTA.vue'

    const props = defineProps<{
      singlePage: SinglePage
    }>()

    const cmsInfo = await cmsUtils.getCmsInfo()
    const route = useRoute()
    const baseUrl = (cmsInfo.website.https ? 'https://' : 'http://') + cmsInfo.website.domain
    const canonicalUrl = baseUrl + route.path.replace('.html', '')

    useHead({
      script: [
        {
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: props.singlePage.title,
            description: props.singlePage.description,
            url: canonicalUrl,
            mainEntity: {
              '@type': 'ItemList',
              name: 'Leafly Packaging Authors',
              description: 'Industry experts who write about packaging processes, international trade, CBD packaging, and digital marketing.',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  item: {
                    '@type': 'Person',
                    name: 'Joanna',
                    jobTitle: 'Sales Director',
                    description: 'Packaging industry expert and author specializing in packaging processes, materials, and design.',
                    sameAs: 'https://www.linkedin.com/in/cannabisvaping/',
                    worksFor: {
                      '@type': 'Organization',
                      name: 'Leafly Packaging'
                    }
                  }
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  item: {
                    '@type': 'Person',
                    name: 'Joyce',
                    jobTitle: 'Sales Manager',
                    description: 'International trade specialist and author covering US packaging regulations and cannabis compliance.',
                    sameAs: 'https://www.linkedin.com/in/joycewu1231/',
                    worksFor: {
                      '@type': 'Organization',
                      name: 'Leafly Packaging'
                    }
                  }
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  item: {
                    '@type': 'Person',
                    name: 'Jane',
                    jobTitle: 'Sales Manager',
                    description: 'CBD packaging specialist and author writing about Mylar bags, Rigid boxes, CR packaging, and vape packaging.',
                    sameAs: 'https://www.linkedin.com/in/jane-chen-a4036318a/',
                    worksFor: {
                      '@type': 'Organization',
                      name: 'Leafly Packaging'
                    }
                  }
                },
                {
                  '@type': 'ListItem',
                  position: 4,
                  item: {
                    '@type': 'Person',
                    name: 'Jsper Fang',
                    jobTitle: 'CRM & Packaging Process Specialist',
                    description: 'Digital marketing expert and author focusing on packaging processes, SEO, and content strategy.',
                    sameAs: 'https://www.linkedin.com/in/fangyuchuang',
                    worksFor: {
                      '@type': 'Organization',
                      name: 'Leafly Packaging'
                    }
                  }
                }
              ]
            }
          })
        }
      ]
    })

    // 团队数据亮点
    const teamStats = ref([
      { label: 'Years Combined Experience', value: '40+' },
      { label: 'Countries Served', value: '30+' },
      { label: 'Articles Written', value: '200+' },
      { label: 'Happy Clients', value: '500+' }
    ])

    // 作者数据
    const authors = ref([
      {
        id: 1,
        name: 'Joanna',
        position: 'Sales Director',
        imageUrl: `/leaflypackaging-com/images/why-leafly/img-01.jpg`,
        linkedin: 'https://www.linkedin.com/in/cannabisvaping/',
        expertise: ['Packaging Processes', 'Material Science', 'Packaging Design', 'Market Analysis', 'Industry Trends']
      },
      {
        id: 2,
        name: 'Joyce',
        position: 'Sales Manager',
        imageUrl: `/leaflypackaging-com/images/why-leafly/img-02.jpg`,
        linkedin: 'https://www.linkedin.com/in/joycewu1231/',
        expertise: ['US Market Compliance', 'Cannabis Packaging', 'International Trade', 'Client Relations']
      },
      {
        id: 3,
        name: 'Jane',
        position: 'Sales Manager',
        imageUrl: `/leaflypackaging-com/images/why-leafly/img-03.jpg`,
        linkedin: 'https://www.linkedin.com/in/jane-chen-a4036318a/',
        expertise: ['Mylar Bag', 'Rigid Box', 'Structural Design', 'CBD Packaging', 'Child-Resistant']
      },
      {
        id: 5,
        name: 'Jsper Fang',
        position: 'CRM & Packaging Process Specialist',
        imageUrl: `/leaflypackaging-com/images/why-leafly/img-05.jpg`,
        linkedin: 'https://www.linkedin.com/in/fangyuchuang',
        expertise: ['Packaging Processes', 'Digital Marketing', 'SEO Strategy', 'CRM Management', 'Content Strategy']
      }
    ])

    // 解构每个作者供模板使用（避免 TS 索引访问报错）
    const joanna = computed(() => authors.value[0]!)
    const joyce = computed(() => authors.value[1]!)
    const jane = computed(() => authors.value[2]!)
    const jsper = computed(() => authors.value[3]!)
</script>

<style scoped lang="scss">

</style>
